import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [processingInvoices, setProcessingInvoices] = useState(new Set());
  const [resolveTarget, setResolveTarget] = useState(null); // booking under cancel/reschedule
  const [resolveAction, setResolveAction] = useState(null); // 'cancel_no_fee' | 'cancel_with_fee' | 'reschedule_no_fee' | 'reschedule_with_fee'
  const [resolveNewDate, setResolveNewDate] = useState('');
  const [resolveNewTime, setResolveNewTime] = useState('');
  const [resolveReason, setResolveReason] = useState('');
  const [resolving, setResolving] = useState(false);
  const navigate = useNavigate();
  const itemsPerPage = 20;

  useEffect(() => {
    loadBookings();
  }, [filter]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const loadBookings = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const url = filter === 'all' 
        ? `${backendUrl}/api/admin/bookings`
        : `${backendUrl}/api/admin/bookings?status=${filter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    try {
      await fetch(`${backendUrl}/api/admin/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      loadBookings();
    } catch (error) {
      alert('Failed to update booking');
    }
  };

  const completeAndInvoice = async (bookingId, bookingData) => {
    if (!window.confirm('Mark this booking as completed and create a Xero invoice?')) {
      return;
    }

    // Mark this booking as processing to prevent duplicate clicks
    setProcessingInvoices(prev => new Set([...prev, bookingId]));

    const token = localStorage.getItem('admin_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    try {
      const response = await fetch(`${backendUrl}/api/admin/bookings/${bookingId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (result.success) {
        alert(`✅ Booking completed!\n\nInvoice ${result.invoice_number} created and sent to ${bookingData.invoice_email || bookingData.customer_email}`);
        loadBookings();
      } else {
        alert(`⚠️ Booking marked as completed, but invoice creation failed:\n${result.error}\n\nPlease create the invoice manually in Xero.`);
        loadBookings();
      }
    } catch (error) {
      alert('Failed to complete booking and create invoice');
      console.error(error);
    } finally {
      // Remove from processing set after completion
      setProcessingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(bookingId);
        return newSet;
      });
    }
  };

  const deleteBooking = async (bookingId, customerName) => {
    if (!window.confirm(`Are you sure you want to delete the booking for ${customerName}?\n\nThis action cannot be undone.`)) {
      return;
    }

    const token = localStorage.getItem('admin_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    try {
      const response = await fetch(`${backendUrl}/api/admin/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ Booking deleted successfully');
        loadBookings();
      } else {
        alert('Failed to delete booking');
      }
    } catch (error) {
      alert('Failed to delete booking');
      console.error(error);
    }
  };

  const openResolveDialog = (booking) => {
    setResolveTarget(booking);
    setResolveAction(null);
    setResolveNewDate('');
    setResolveNewTime('');
    setResolveReason('');
  };

  const closeResolveDialog = () => {
    if (resolving) return;
    setResolveTarget(null);
    setResolveAction(null);
    setResolveNewDate('');
    setResolveNewTime('');
    setResolveReason('');
  };

  // Compute hours-until-booking for the active target (used to show late-fee toggle)
  const hoursUntilTarget = (() => {
    if (!resolveTarget?.booking_date || !resolveTarget?.booking_time) return null;
    try {
      // booking is in Brisbane (UTC+10, no DST)
      const [hh, mm] = String(resolveTarget.booking_time).split(':').map((s) => parseInt(s, 10));
      const [y, m, d] = resolveTarget.booking_date.split('-').map((s) => parseInt(s, 10));
      const startUtcMs = Date.UTC(y, (m || 1) - 1, d || 1, (hh || 0) - 10, mm || 0, 0);
      return (startUtcMs - Date.now()) / (1000 * 60 * 60);
    } catch {
      return null;
    }
  })();
  const targetIsLate = hoursUntilTarget !== null && hoursUntilTarget < 24;

  const submitResolve = async () => {
    if (!resolveTarget || !resolveAction) return;

    // Map the 4 UI actions to the 3 backend actions
    const apiAction = resolveAction.startsWith('reschedule') ? 'reschedule' : resolveAction;
    const applyLateFee = resolveAction === 'reschedule_with_fee';

    if (apiAction === 'reschedule' && (!resolveNewDate || !resolveNewTime)) {
      alert('Please enter the new date and time.');
      return;
    }
    if (resolveAction === 'cancel_with_fee' &&
        !window.confirm('This will cancel the booking AND issue a Xero invoice for $220 (inc GST). Continue?')) {
      return;
    }
    if (resolveAction === 'reschedule_with_fee' &&
        !window.confirm('This will reschedule the booking and add a $220 (inc GST) late reschedule fee to the final invoice. Continue?')) {
      return;
    }

    const token = localStorage.getItem('admin_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    setResolving(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/bookings/${resolveTarget.id}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: apiAction,
          new_date: resolveNewDate,
          new_time: resolveNewTime,
          reason: resolveReason,
          apply_late_fee: apiAction === 'reschedule' ? applyLateFee : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.detail || 'Failed to resolve booking');
        return;
      }
      const successMsg =
        resolveAction === 'cancel_no_fee'
          ? '✅ Booking cancelled (no fee).'
          : resolveAction === 'cancel_with_fee'
            ? `✅ Booking cancelled and $220 invoice ${data?.invoice?.invoice_number ? '#' + data.invoice.invoice_number + ' ' : ''}issued.`
            : data.late_fee_applied
              ? `✅ Booking rescheduled to ${data.new_date} at ${data.new_time}. $220 late fee will be added to the final invoice.`
              : `✅ Booking rescheduled to ${data.new_date} at ${data.new_time}.`;
      alert(successMsg);
      closeResolveDialog();
      loadBookings();
    } catch (e) {
      alert('Network error. Please try again.');
    } finally {
      setResolving(false);
    }
  };


  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  // Calculate pagination
  const totalPages = Math.ceil(bookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBookings = bookings.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="https://images.squarespace-cdn.com/content/v1/6329980709c417765c18ef9c/1663928993488-9PHELX1GHR1MGT4SNOLP/minchio+brothers+logo.png?format=100w" alt="Logo" className="h-10 w-10" />
              <h1 className="text-xl font-bold text-gray-900">Bookings</h1>
            </div>
            <button onClick={handleLogout} className="text-red-600 hover:text-red-700">
              <i className="ri-logout-box-line"></i> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="flex">
        <div className="w-64 bg-white h-screen shadow-sm">
          <nav className="p-4 space-y-2">
            <Link to="/admin/dashboard" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-dashboard-line text-xl"></i><span>Dashboard</span>
            </Link>
            <Link to="/admin/bookings" className="flex items-center space-x-3 px-4 py-3 bg-blue-50 text-blue-700 rounded-lg font-medium">
              <i className="ri-calendar-check-line text-xl"></i><span>Bookings</span>
            </Link>
            <Link to="/admin/contacts" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-message-3-line text-xl"></i><span>Contacts</span>
            </Link>
            <Link to="/admin/reviews" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-star-line text-xl"></i><span>Reviews</span>
            </Link>
            <Link to="/admin/settings" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-settings-3-line text-xl"></i><span>Settings</span>
            </Link>
          </nav>
        </div>

        <div className="flex-1 p-8">
          <div className="mb-6 flex justify-between items-center">
            <div className="flex space-x-2">
              <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-lg ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}>All</button>
              <button onClick={() => setFilter('pending')} className={`px-4 py-2 rounded-lg ${filter === 'pending' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}>Pending</button>
              <button onClick={() => setFilter('confirmed')} className={`px-4 py-2 rounded-lg ${filter === 'confirmed' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}>Confirmed</button>
              <button onClick={() => setFilter('completed')} className={`px-4 py-2 rounded-lg ${filter === 'completed' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}>Completed</button>
            </div>
            <div className="text-sm text-gray-600">
              Total: {bookings.length} bookings
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : bookings.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No bookings found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Property Address</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size (sqm)</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {currentBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">{booking.customer_name}</div>
                            {booking.client_id && (
                              <span data-testid={`portal-badge-${booking.id}`} title="Booked via Client Portal" className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">
                                Portal
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{booking.customer_email}</div>
                          {booking.invoice_email && booking.invoice_email !== booking.customer_email && (
                            <div className="text-xs text-blue-600">Invoice: {booking.invoice_email}</div>
                          )}
                          <div className="text-sm text-gray-500">{booking.customer_phone}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{booking.property_address || 'Not provided'}</div>
                          {Number(booking.package?.travel_fee) > 0 && (
                            <div className="text-xs text-amber-700 font-medium mt-0.5">
                              🚗 +A${Number(booking.package.travel_fee).toFixed(0)} travel fee
                              {booking.package?.travel_duration_minutes ? ` (~${Math.round(booking.package.travel_duration_minutes)} min)` : ''}
                            </div>
                          )}
                          {booking.package?.travel_fee_verified === false && (
                            <div
                              data-testid={`travel-fee-unverified-${booking.id}`}
                              title="Google Maps could not confirm the drive time for this address - please check the distance manually and adjust the invoice if needed."
                              className="text-xs text-rose-700 font-semibold mt-0.5 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5 inline-block"
                            >
                              ⚠ Travel fee not verified - check manually
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {booking.property_size_sqm ? (
                            <div>
                              <div className="text-sm text-gray-900">{Number(booking.property_size_sqm).toLocaleString()} sqm</div>
                              {Number(booking.large_property_surcharge) > 0 && (
                                <div className="text-xs text-amber-700 font-medium">
                                  +A${Number(booking.large_property_surcharge).toFixed(0)} surcharge
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-400">—</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{booking.package?.package_name}</div>
                          <div className="text-sm text-gray-500">A${booking.package?.total_price} incl GST</div>
                          {booking.package?.add_ons && booking.package.add_ons.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">+ {booking.package.add_ons.join(', ')}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{booking.booking_date}</div>
                          <div className="text-sm text-gray-500">{booking.booking_time}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            booking.status === 'cancelled' || booking.status === 'cancelled_no_fee' ? 'bg-gray-100 text-gray-700' :
                            booking.status === 'cancelled_with_fee' ? 'bg-red-100 text-red-800' :
                            booking.status === 'cancellation_requested' || booking.status === 'reschedule_requested_late' ? 'bg-amber-100 text-amber-900' :
                            booking.status === 'rescheduled' ? 'bg-indigo-100 text-indigo-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {booking.status?.replaceAll('_', ' ')}
                          </span>
                          {(booking.status === 'cancellation_requested' || booking.status === 'reschedule_requested_late') && (
                            <div className="text-[11px] text-amber-700 mt-1 font-medium">⚠ Customer requested — needs review</div>
                          )}
                          {Number(booking.package?.late_reschedule_fee) > 0 && booking.status !== 'completed' && (
                            <div className="text-[11px] text-red-700 mt-1 font-medium" data-testid="late-fee-pending-badge">
                              💰 +$220 late reschedule fee on final invoice
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {booking.xero_invoice_number ? (
                            <div className="text-xs">
                              <div className="text-green-600 font-medium">✓ {booking.xero_invoice_number}</div>
                              <div className="text-gray-500">Sent to Xero</div>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400">No invoice</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col space-y-2">
                            {booking.status !== 'completed' && !booking.xero_invoice_id && !booking.xero_invoice_number && !processingInvoices.has(booking.id) && (
                              <button
                                onClick={() => completeAndInvoice(booking.id, booking)}
                                disabled={processingInvoices.has(booking.id)}
                                className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <i className="ri-check-line"></i> Complete & Invoice
                              </button>
                            )}
                            {processingInvoices.has(booking.id) && (
                              <div className="px-3 py-1.5 bg-blue-100 text-blue-700 text-xs rounded text-center">
                                <i className="ri-loader-4-line animate-spin"></i> Processing...
                              </div>
                            )}
                            <select
                              value={booking.status}
                              onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                              className="text-xs border border-gray-300 rounded px-2 py-1"
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                            <button
                              onClick={() => openResolveDialog(booking)}
                              className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded hover:bg-amber-700 font-medium"
                              data-testid={`resolve-booking-${booking.id}`}
                            >
                              <i className="ri-calendar-close-line"></i> Cancel / Reschedule
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination Controls */}
            {!loading && bookings.length > itemsPerPage && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, bookings.length)}</span> of{' '}
                  <span className="font-medium">{bookings.length}</span> bookings
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <i className="ri-arrow-left-s-line"></i> Previous
                  </button>
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`px-3 py-1 border rounded text-sm ${
                            currentPage === page
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-2">...</span>;
                    }
                    return null;
                  })}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel / Reschedule Modal */}
      {resolveTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="resolve-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">Cancel or Reschedule Booking</h3>
              <button onClick={closeResolveDialog} className="text-gray-400 hover:text-gray-600 text-2xl" disabled={resolving}>×</button>
            </div>
            <div className="p-6 space-y-5">
              <div className="bg-gray-50 rounded-lg p-4 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Customer</span><span className="font-medium">{resolveTarget.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Property</span><span className="font-medium text-right">{resolveTarget.property_address || 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Date / Time</span><span className="font-medium">{resolveTarget.booking_date} {resolveTarget.booking_time}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Package</span><span className="font-medium">{resolveTarget.package?.package_name}</span></div>
                {resolveTarget.cancellation_reason && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-gray-500 mb-1">Customer reason</div>
                    <div className="text-gray-800">{resolveTarget.cancellation_reason}</div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
                <div className="grid grid-cols-1 gap-2">
                  <ActionRadio
                    label="Cancel — No Fee"
                    description="Customer is cancelling >24h out, weather waiver, or other goodwill."
                    value="cancel_no_fee"
                    selected={resolveAction}
                    onChange={setResolveAction}
                  />
                  <ActionRadio
                    label="Cancel — Charge $220 Fee"
                    description="Late cancellation. Issues a Xero invoice with original line items (zeroed) + $220 inc GST fee."
                    value="cancel_with_fee"
                    selected={resolveAction}
                    onChange={setResolveAction}
                    recommended={targetIsLate}
                  />
                  <ActionRadio
                    label="Reschedule — No Fee"
                    description="Move the booking with no extra charge. Calendar event moves and customer is emailed."
                    value="reschedule_no_fee"
                    selected={resolveAction}
                    onChange={setResolveAction}
                    recommended={!targetIsLate}
                  />
                  <ActionRadio
                    label="Reschedule — Charge $220 Fee"
                    description="Late reschedule. $220 (inc GST) is automatically added to the final invoice when you click Complete & Invoice."
                    value="reschedule_with_fee"
                    selected={resolveAction}
                    onChange={setResolveAction}
                    recommended={targetIsLate}
                  />
                </div>
              </div>

              {(resolveAction === 'reschedule_no_fee' || resolveAction === 'reschedule_with_fee') && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New date</label>
                    <input
                      type="date"
                      value={resolveNewDate}
                      onChange={(e) => setResolveNewDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      data-testid="admin-resched-date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New time</label>
                    <input
                      type="text"
                      value={resolveNewTime}
                      onChange={(e) => setResolveNewTime(e.target.value)}
                      placeholder="e.g. 10:00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      data-testid="admin-resched-time"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Internal note (optional)</label>
                <textarea
                  rows="2"
                  value={resolveReason}
                  onChange={(e) => setResolveReason(e.target.value)}
                  placeholder="Add a note for the booking record (e.g. 'Weather waiver - storm warning')"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  onClick={closeResolveDialog}
                  disabled={resolving}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  Close
                </button>
                <button
                  onClick={submitResolve}
                  disabled={resolving || !resolveAction}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="resolve-submit"
                >
                  {resolving ? 'Working...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ActionRadio = ({ label, description, value, selected, onChange, recommended = false }) => (
  <label
    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${
      selected === value ? 'border-amber-600 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'
    }`}
  >
    <input
      type="radio"
      name="resolve-action"
      value={value}
      checked={selected === value}
      onChange={() => onChange(value)}
      className="mt-1"
      data-testid={`action-${value}`}
    />
    <div className="text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-gray-900">{label}</span>
        {recommended && (
          <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
            Recommended
          </span>
        )}
      </div>
      <div className="text-gray-500 mt-1">{description}</div>
    </div>
  </label>
);
